// src/stores/customerChatStore.js
//
// Customer live chat store.
// Consumer-side: unauthenticated visitors start chats, send messages.
// Agent-side: authenticated agents see conversations, reply, manage.

import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import {
  collection,
  doc,
  setDoc,
  addDoc,
  updateDoc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  limit,
  serverTimestamp,
  arrayUnion,
  arrayRemove,
  runTransaction,
} from 'firebase/firestore'
import { onSnapshot } from '@/services/sessionListeners'
import { getAuth } from 'firebase/auth'
import { db } from '@/services/firebaseconfig'
import { logEvent } from '@/utils/firebase/eventLogger'
import { playCustomerChatTone } from '@/utils/audioTones'
import { newCustomerChatId } from '@/utils/customerChatId'

export const useCustomerChatStore = defineStore('customerChat', () => {
  // ── State ─────────────────────────────────────────────────────────────────
  const conversations = ref([])
  const activeConversationId = ref(null)
  const activeMessages = ref([])
  const agentsOnline = ref(false)
  const isInitialized = ref(false)
  // Per-agent read state: chatId → lastReadAt (Timestamp|null), mirrored from
  // customerChats/{chatId}/readStatus/{myUid}. Read state is PER USER — one
  // agent reading a chat clears only their own badge (matches user-to-user
  // chat semantics; before this, the badge was the shared 'waiting' count and
  // opening a chat wrote nothing at all).
  const myReadStatus = ref({})

  let _convoUnsub = null
  let _msgUnsub = null
  let _availUnsub = null
  const _readStatusUnsubs = new Map()

  // ── Computed ──────────────────────────────────────────────────────────────

  const waitingCount = computed(
    () => conversations.value.filter(c => c.status === 'waiting').length
  )

  const activeConversations = computed(() => conversations.value.filter(c => c.status !== 'closed'))

  const tsMillis = t => t?.toMillis?.() ?? (t ? new Date(t).getTime() || 0 : 0)

  // Unread-for-ME: the latest message is from the customer and is newer than
  // my own lastReadAt for that thread. Independent of claim/status.
  function isUnreadForMe(convo) {
    if (!convo || convo.lastMessageSender !== 'customer') return false
    return tsMillis(convo.lastMessageAt) > tsMillis(myReadStatus.value[convo.id])
  }

  const totalUnreadCount = computed(() => conversations.value.filter(isUnreadForMe).length)

  const activeConversation = computed(
    () => conversations.value.find(c => c.id === activeConversationId.value) || null
  )

  // ═══════════════════════════════════════════════════════════════════════════
  // AGENT-SIDE METHODS (authenticated)
  // ═══════════════════════════════════════════════════════════════════════════

  function initialize() {
    if (_convoUnsub) return
    let firstLoad = true
    const q = query(
      collection(db, 'customerChats'),
      where('status', 'in', ['waiting', 'active']),
      orderBy('lastMessageAt', 'desc')
    )
    _convoUnsub = onSnapshot(
      q,
      snap => {
        const prev = conversations.value
        const updated = snap.docs.map(d => ({ id: d.id, ...d.data() }))

        // Detect new waiting chats or new customer messages (skip initial snapshot only)
        if (!firstLoad) {
          const newWaiting = updated.filter(
            c => c.status === 'waiting' && !prev.find(p => p.id === c.id)
          )
          // Compare by lastMessageAt, not preview text — two consecutive
          // identical customer messages ("hello", "hello") keep the preview
          // unchanged and used to silently skip the chime
          // (bugReports/glHpS4I1ZZudTP6Sbuyf). The timestamp always advances.
          const toMillis = t => t?.toMillis?.() ?? (t ? new Date(t).getTime() || 0 : 0)
          const newCustomerMessages = updated.filter(c => {
            const old = prev.find(p => p.id === c.id)
            return (
              old &&
              c.lastMessageSender === 'customer' &&
              toMillis(c.lastMessageAt) > toMillis(old.lastMessageAt)
            )
          })
          if (newWaiting.length > 0 || newCustomerMessages.length > 0) {
            console.log('[customerChatStore] New customer chat activity — playing notification')
            try {
              playCustomerChatTone()
            } catch (_) {
              // ignore — best-effort notification tone, failure is non-critical
            }
          }
        }

        conversations.value = updated
        syncReadStatusListeners(updated)
        isInitialized.value = true
        firstLoad = false
      },
      err => {
        console.error('[customerChatStore] Conversations listener error:', err)
      }
    )
  }

  // Keep one listener per visible thread on MY readStatus doc; drop listeners
  // for threads that left the query (closed).
  function syncReadStatusListeners(list) {
    const uid = getAuth().currentUser?.uid
    if (!uid) return
    const liveIds = new Set(list.map(c => c.id))
    for (const [id, unsub] of _readStatusUnsubs) {
      if (!liveIds.has(id)) {
        unsub()
        _readStatusUnsubs.delete(id)
        if (id in myReadStatus.value) {
          const next = { ...myReadStatus.value }
          delete next[id]
          myReadStatus.value = next
        }
      }
    }
    for (const c of list) {
      if (_readStatusUnsubs.has(c.id)) continue
      const rRef = doc(db, 'customerChats', c.id, 'readStatus', uid)
      _readStatusUnsubs.set(
        c.id,
        onSnapshot(
          rRef,
          s => {
            myReadStatus.value = {
              ...myReadStatus.value,
              [c.id]: s.exists() ? s.data().lastReadAt : null,
            }
          },
          err => {
            console.warn('[customerChatStore] readStatus listener error:', err?.message)
          }
        )
      )
    }
  }

  // Stamp MY read marker for a thread. The doc itself is the audit record
  // (who = doc id, when = lastReadAt).
  async function markThreadRead(chatId) {
    const user = getAuth().currentUser
    if (!user || !chatId) return
    try {
      await setDoc(
        doc(db, 'customerChats', chatId, 'readStatus', user.uid),
        { uid: user.uid, lastReadAt: serverTimestamp() },
        { merge: true }
      )
    } catch (err) {
      console.warn('[customerChatStore] markThreadRead failed:', err?.message)
    }
  }

  function openConversation(chatId) {
    activeConversationId.value = chatId
    if (_msgUnsub) {
      _msgUnsub()
      _msgUnsub = null
    }

    markThreadRead(chatId)
    const q = query(
      collection(db, 'customerChats', chatId, 'messages'),
      orderBy('createdAt', 'asc')
    )
    _msgUnsub = onSnapshot(q, snap => {
      activeMessages.value = snap.docs.map(d => ({ id: d.id, ...d.data() }))
      // Messages arriving while I'm viewing this thread are read the moment
      // they render — keep my marker current so the badge never lights for
      // the thread I have open.
      if (activeConversationId.value === chatId && !snap.empty) {
        markThreadRead(chatId)
      }
    })
  }

  function closeConversation() {
    activeConversationId.value = null
    activeMessages.value = []
    if (_msgUnsub) {
      _msgUnsub()
      _msgUnsub = null
    }
  }

  async function sendAgentMessage(chatId, text, agentName) {
    if (!text.trim()) return
    await addDoc(collection(db, 'customerChats', chatId, 'messages'), {
      text: text.trim(),
      sender: 'agent',
      senderName: agentName,
      createdAt: serverTimestamp(),
    })
    await updateDoc(doc(db, 'customerChats', chatId), {
      lastMessageAt: serverTimestamp(),
      lastMessagePreview: text.trim().substring(0, 100),
      lastMessageSender: 'agent',
    })
  }

  async function assignToMe(chatId, agentId, agentName) {
    await updateDoc(doc(db, 'customerChats', chatId), {
      agentId,
      agentName,
      status: 'active',
    })
  }

  async function markClosed(chatId) {
    await updateDoc(doc(db, 'customerChats', chatId), { status: 'closed' })
    if (activeConversationId.value === chatId) closeConversation()
  }

  async function addLabel(chatId, label) {
    await updateDoc(doc(db, 'customerChats', chatId), { labels: arrayUnion(label) })
  }

  async function removeLabel(chatId, label) {
    await updateDoc(doc(db, 'customerChats', chatId), { labels: arrayRemove(label) })
  }

  async function linkToCustomer(chatId, customerId) {
    if (!chatId || !customerId) throw new Error('Choose a chat and customer first.')
    const chatRef = doc(db, 'customerChats', chatId)
    const changed = await runTransaction(db, async transaction => {
      const chat = await transaction.get(chatRef)
      const customer = await transaction.get(doc(db, 'customers', customerId))
      if (!chat.exists() || !customer.exists())
        throw new Error('Chat or customer no longer exists.')
      const currentId = chat.data().customerId
      if (currentId === customerId) return false
      if (currentId) throw new Error('This chat is already linked to another customer.')
      transaction.update(chatRef, { customerId })
      return true
    })
    if (changed)
      await logEvent({
        eventType: 'CHAT_LINKED_TO_CUSTOMER',
        action: 'update',
        resource: { type: 'customerChat', id: chatId },
        changes: { before: { customerId: null }, after: { customerId }, fields: ['customerId'] },
        context: { metadata: { source: 'customerChatStore', customerId } },
      })
  }

  async function toggleAutoSuggest(chatId, value) {
    await updateDoc(doc(db, 'customerChats', chatId), { autoSuggest: !!value })
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // CONSUMER-SIDE METHODS (unauthenticated)
  // ═══════════════════════════════════════════════════════════════════════════

  async function startChat(name, email, pageUrl) {
    const chatId = newCustomerChatId('visitor')
    const sessionId = chatId // same as chatId for simplicity

    await setDoc(doc(db, 'customerChats', chatId), {
      customerName: name,
      customerEmail: email || '',
      status: 'waiting',
      agentId: null,
      agentName: null,
      customerId: null,
      labels: [],
      createdAt: serverTimestamp(),
      lastMessageAt: serverTimestamp(),
      lastMessagePreview: '',
      lastMessageSender: 'customer',
      pageUrl: pageUrl || '',
      sessionId,
      autoSuggest: true,
    })

    // Add system message
    await addDoc(collection(db, 'customerChats', chatId, 'messages'), {
      text: `${name} started a chat`,
      sender: 'system',
      senderName: 'System',
      createdAt: serverTimestamp(),
    })

    return chatId
  }

  async function sendCustomerMessage(chatId, text, senderName) {
    if (!text.trim()) return
    await addDoc(collection(db, 'customerChats', chatId, 'messages'), {
      text: text.trim(),
      sender: 'customer',
      senderName,
      createdAt: serverTimestamp(),
    })
    await updateDoc(doc(db, 'customerChats', chatId), {
      lastMessageAt: serverTimestamp(),
      lastMessagePreview: text.trim().substring(0, 100),
      lastMessageSender: 'customer',
    })
  }

  function listenToMessages(chatId) {
    if (_msgUnsub) {
      _msgUnsub()
      _msgUnsub = null
    }
    const q = query(
      collection(db, 'customerChats', chatId, 'messages'),
      orderBy('createdAt', 'asc')
    )
    _msgUnsub = onSnapshot(q, snap => {
      activeMessages.value = snap.docs.map(d => ({ id: d.id, ...d.data() }))
    })
  }

  /**
   * Backfill a chat's customer identity when we know it and the stored doc
   * doesn't. Chats opened before the portal passed the customer's name were
   * written as "Portal Customer", and staff had no way to tell who they were
   * talking to even though the person was authenticated.
   *
   * Must be callable from BOTH entry points: starting/reusing a chat, and
   * RESTORING one from localStorage on refresh. The restore path skips
   * startPortalChat entirely (it just re-attaches the listener), so putting the
   * heal only there meant it never ran for an already-open conversation — which
   * is exactly the case that needed it.
   *
   * Cosmetic by design: a failure here must never stop a chat from opening.
   */
  async function ensureChatIdentity(chatId, customerName, customerEmail) {
    if (!chatId || !customerName) return
    try {
      const ref = doc(db, 'customerChats', chatId)
      const snap = await getDoc(ref)
      if (!snap.exists()) return
      const data = snap.data()
      const isPlaceholder = !data.customerName || data.customerName === 'Portal Customer'
      if (!isPlaceholder) return
      await updateDoc(ref, {
        customerName,
        ...(customerEmail && !data.customerEmail ? { customerEmail } : {}),
      })
    } catch (err) {
      console.warn('[customerChatStore] Could not backfill chat identity:', err?.message)
    }
  }

  /**
   * Start a chat as an authenticated portal customer.
   * customerId is known upfront — no pre-chat form needed.
   * Returns existing active chatId if one exists.
   */
  async function startPortalChat(customerId, customerName, customerEmail) {
    // Check for existing active chat
    const existingQ = query(
      collection(db, 'customerChats'),
      where('customerId', '==', customerId),
      where('status', 'in', ['waiting', 'active']),
      orderBy('createdAt', 'desc'),
      limit(1)
    )
    const snap = await getDocs(existingQ)
    if (!snap.empty) {
      const existingId = snap.docs[0].id
      await ensureChatIdentity(existingId, customerName, customerEmail)
      listenToMessages(existingId)
      return existingId
    }

    const chatId = newCustomerChatId('portal')

    // Phase 51 — capture the authenticated portal user's Firebase Auth UID so
    // onChatMessageSync can tag customer-origin events with `actor.type: 'customer'`.
    // Portal customers are always authenticated when reaching startPortalChat.
    const authUid = getAuth().currentUser?.uid || null

    await setDoc(doc(db, 'customerChats', chatId), {
      customerName: customerName || 'Portal Customer',
      customerEmail: customerEmail || '',
      customerUid: authUid,
      status: 'waiting',
      agentId: null,
      agentName: null,
      customerId,
      labels: [],
      createdAt: serverTimestamp(),
      lastMessageAt: serverTimestamp(),
      lastMessagePreview: '',
      lastMessageSender: 'customer',
      pageUrl: window.location.href,
      sessionId: chatId,
      source: 'portal',
      autoSuggest: true,
    })

    await addDoc(collection(db, 'customerChats', chatId, 'messages'), {
      text: `${customerName || 'Customer'} started a chat`,
      sender: 'system',
      senderName: 'System',
      createdAt: serverTimestamp(),
    })

    listenToMessages(chatId)
    return chatId
  }

  function subscribeToAvailability() {
    if (_availUnsub) return

    let resolved = false
    const docRef = doc(db, 'publicConfig', 'chatAvailability')

    // Race: HTTP fetch immediately
    import('@/utils/publicConfigLoader')
      .then(({ fetchPublicConfig }) => {
        fetchPublicConfig(['chatAvailability'])
          .then(data => {
            if (!resolved) {
              resolved = true
              agentsOnline.value = data?.chatAvailability?.online !== false
            }
          })
          .catch(() => {
            if (!resolved) agentsOnline.value = true
          })
      })
      .catch(() => {
        if (!resolved) agentsOnline.value = true
      })

    // Also try onSnapshot
    _availUnsub = onSnapshot(
      docRef,
      snap => {
        resolved = true
        agentsOnline.value = snap.exists() ? snap.data().online !== false : true
      },
      () => {
        // Firestore blocked — HTTP already handled it
        if (!resolved) agentsOnline.value = true
      }
    )
  }

  // ── Cleanup ───────────────────────────────────────────────────────────────

  function cleanup() {
    if (_convoUnsub) {
      _convoUnsub()
      _convoUnsub = null
    }
    if (_msgUnsub) {
      _msgUnsub()
      _msgUnsub = null
    }
    if (_availUnsub) {
      _availUnsub()
      _availUnsub = null
    }
    for (const unsub of _readStatusUnsubs.values()) unsub()
    _readStatusUnsubs.clear()
    myReadStatus.value = {}
    conversations.value = []
    activeMessages.value = []
    activeConversationId.value = null
    isInitialized.value = false
  }

  return {
    conversations,
    activeConversationId,
    activeMessages,
    activeConversation,
    agentsOnline,
    isInitialized,
    waitingCount,
    activeConversations,
    totalUnreadCount,
    myReadStatus,
    isUnreadForMe,
    // Agent
    initialize,
    openConversation,
    markThreadRead,
    closeConversation,
    sendAgentMessage,
    assignToMe,
    markClosed,
    addLabel,
    removeLabel,
    linkToCustomer,
    toggleAutoSuggest,
    // Consumer
    startChat,
    sendCustomerMessage,
    listenToMessages,
    subscribeToAvailability,
    startPortalChat,
    ensureChatIdentity,
    // Shared
    cleanup,
  }
})
